import { cloneDeep } from 'lodash-es';
import { mapPageSortByForListApi } from '@/helpers/mapPageSortByForListApi';
import { ToolUserItem } from '@/models/toolUser';
import { createToolUserDetailsPageHeaders } from '@/pages/admin/toolUserDetails/ToolUserDetailsPageHeaders';
import { isRequestCanceled } from '@/services/baseService';
import { toolUserService } from '@/services/toolUserService';
import { createLoadingGenerationTracker } from '@/stores/composables/createLoadingGenerationTracker';
import { createStorePersistConfig } from '@/stores/composables/createStorePersistConfig';
import { useFilterPanelState } from '@/stores/composables/useFilterPanelState';
import { useFilterPersistence } from '@/stores/composables/useFilterPersistence';
import { IListParams, IPageOptions } from '@/types';
import type { IToolUserMutationResponse } from '@/types/toolUser';
import { IToolUserDetailFilters } from '@/types/toolUserDetail';
import type { DataTableHeader } from '@/types/vuetify';

const defaultSortBy = [{ key: 'created_at', order: 'desc' as const }];

function createToolUserDetailStoreSetup(storeId: string) {
    // --- state ---
    const items = shallowRef<ToolUserItem[]>([]);
    const total = ref<number>(0);
    const loading = ref(false);
    const loadGeneration = createLoadingGenerationTracker();
    const mailActionLoading = ref(false);
    const deleteLoading = ref(false);

    // --- filters ---
    const filters = reactive<IToolUserDetailFilters>({
        userSearch: {
            value: null,
            title: 'toolUserDetails.filters.search',
            allowSaveState: false,
        },
        userId: {
            value: null,
            title: 'toolUsers.filters.userId',
            allowSaveState: false,
        },
        userOwnerId: {
            value: null,
            title: 'toolUserDetails.filters.agent',
            allowSaveState: true,
        },
        userStatus: {
            value: null,
            title: 'toolUserDetails.filters.status',
            allowSaveState: true,
        },
        userCurrencyId: {
            value: null,
            title: 'toolUserDetails.filters.currency',
            allowSaveState: true,
        },
        userCountryId: {
            value: null,
            title: 'toolUserDetails.filters.country',
            allowSaveState: true,
        },
        userDocumentStatus: {
            value: null,
            title: 'toolUserDetails.filters.documents',
            allowSaveState: true,
        },
    });

    // --- filter panel ---
    const { hiddenFilters, visibleFiltersMobile, filterOrder, availableFilters, useButtonForFiltersApply, saveFiltersState, visibleFilters } =
        useFilterPanelState(filters);

    // --- options ---
    const options = ref<IPageOptions>({
        page: 1,
        itemsPerPage: 100,
        rowsPerPageItems: [100, 200, 500],
        sortBy: [...defaultSortBy],
    });

    // --- reset ---
    function resetFilters() {
        filters.userSearch.value = null;
        filters.userId.value = null;
        filters.userOwnerId.value = null;
        filters.userStatus.value = null;
        filters.userCurrencyId.value = null;
        filters.userCountryId.value = null;
        filters.userDocumentStatus.value = null;
    }

    // --- table ---
    const headers = computed<DataTableHeader[]>(() => createToolUserDetailsPageHeaders());
    const availableColumns = computed(() => headers.value.filter((header) => header.title));
    const hiddenColumns = ref<string[]>([]);
    const tableHeaders = computed<DataTableHeader[]>(() =>
        headers.value.filter((header) => header.key && (!hiddenColumns.value.includes(header.key) || !header.title))
    );

    // --- persistence ---
    const { reloadFiltersFromStorage } = useFilterPersistence({
        storeId,
        primaryStoreId: 'toolUserDetails',
        filters,
        saveFiltersState,
        resetFilters,
        transform: {
            load: (data: Record<string, unknown>) => {
                if (!data || typeof data !== 'object') return data;
                const next = { ...data };
                if ('userCurrency' in next && !('userCurrencyId' in next)) {
                    (next as Record<string, unknown>).userCurrencyId = next.userCurrency;
                    delete next.userCurrency;
                }
                if ('documentsStatus' in next && !('userDocumentStatus' in next)) {
                    (next as Record<string, unknown>).userDocumentStatus = next.documentsStatus;
                    delete next.documentsStatus;
                }
                return next;
            },
        },
    });

    // --- actions ---
    async function loadData() {
        const loadId = loadGeneration.next();
        loading.value = true;
        try {
            const params: IListParams<IToolUserDetailFilters> = {
                filters: cloneDeep(filters),
                options: {
                    sortBy: mapPageSortByForListApi(options.value.sortBy, headers.value),
                    page: options.value.page,
                    itemsPerPage: options.value.itemsPerPage,
                },
                requiredData: [],
                requiredDictionaries: ['Agent', 'Country', 'Currency', 'CurrencyAll', 'AuditTable'],
                requestKey: `tool-user-details:${storeId}`,
            };

            const response = await toolUserService.detailList(params);
            items.value = response.data.rows.map((row) => new ToolUserItem(row));
            total.value = response.data.total;
        } catch (e) {
            if (isRequestCanceled(e)) return;
            throw e;
        } finally {
            if (loadGeneration.isLatest(loadId)) {
                loading.value = false;
            }
        }
    }

    function updateOptions(value: Partial<IPageOptions>) {
        if (value.page !== undefined && value.page !== options.value.page) {
            options.value.page = value.page;
            loadData();
        }
        if (value.itemsPerPage !== undefined && value.itemsPerPage !== options.value.itemsPerPage) {
            options.value.itemsPerPage = value.itemsPerPage;
            options.value.page = 1;
            loadData();
        }
        if (value.sortBy !== undefined) {
            options.value.sortBy = value.sortBy;
            loadData();
        }
    }

    function mergeMutationResponse(response: { data: IToolUserMutationResponse }) {
        if (response.data.success === false) return;
        const row = response.data.row;
        if (!row) return;
        const idx = items.value.findIndex((i) => i.id === row.id);
        if (idx !== -1) {
            items.value[idx] = new ToolUserItem(row);
        } else {
            items.value = [new ToolUserItem(row), ...items.value];
            total.value += 1;
        }
        triggerRef(items);
    }

    async function patchBoolField(item: ToolUserItem, modelKey: 'isIdProof' | 'isUtilityProof' | 'isActive', value: 0 | 1, apiField: string) {
        const oldValue = item[modelKey];
        item[modelKey] = value;
        triggerRef(items);

        try {
            const response = await toolUserService.patch({ rows: [{ id: item.id, [apiField]: value }] });
            mergeMutationResponse(response);
            if (!response.data.row && response.data.success === false) {
                item[modelKey] = oldValue;
                triggerRef(items);
            }
        } catch {
            item[modelKey] = oldValue;
            triggerRef(items);
        }
    }

    async function sendApprovedNotification(item: ToolUserItem) {
        mailActionLoading.value = true;
        try {
            const response = await toolUserService.sendApprovedNotification({ rows: [{ id: item.id }] });
            mergeMutationResponse(response);
        } catch {
            /* toolUserService wraps errors */
        } finally {
            mailActionLoading.value = false;
        }
    }

    async function deleteToolUser(item: ToolUserItem) {
        deleteLoading.value = true;
        try {
            const response = await toolUserService.delete({ rows: [{ id: item.id }] });
            if (response.data.success !== false) {
                await loadData();
            }
        } catch {
            /* toolUserService wraps errors */
        } finally {
            deleteLoading.value = false;
        }
    }

    function clearData() {
        items.value = [];
        total.value = 0;
        options.value.page = 1;
        loading.value = false;
        mailActionLoading.value = false;
        deleteLoading.value = false;
    }

    // --- return ---
    return {
        storeId,
        items,
        total,
        filters,
        hiddenFilters,
        visibleFiltersMobile,
        filterOrder,
        availableFilters,
        useButtonForFiltersApply,
        saveFiltersState,
        visibleFilters,
        resetFilters,
        reloadFiltersFromStorage,
        headers,
        tableHeaders,
        availableColumns,
        hiddenColumns,
        loading,
        mailActionLoading,
        deleteLoading,
        options,
        loadData,
        updateOptions,
        mergeMutationResponse,
        patchBoolField,
        sendApprovedNotification,
        deleteToolUser,
        clearData,
    };
}

export function useToolUserDetailStore(storeId: string = 'toolUserDetails') {
    const useStore = defineStore(storeId, () => createToolUserDetailStoreSetup(storeId), {
        persist: createStorePersistConfig(storeId, 'toolUserDetails'),
    });
    return useStore();
}
